import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, MessageSquare, Users, Phone, Mail, MapPin, Weight, Calendar, Trash2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_STATUS_META = {
  pending:   { color: 'bg-amber-100 text-amber-800',   label: 'Pending' },
  contacted: { color: 'bg-blue-100 text-blue-800',     label: 'Contacted' },
  won:       { color: 'bg-green-100 text-green-800',   label: 'Won' },
  dismissed: { color: 'bg-zinc-200 text-zinc-600',     label: 'Dismissed' },
};

export const QuotesTab = ({ quotes: initialQuotes }) => {
  const [quotes, setQuotes] = useState(initialQuotes || []);
  const [filter, setFilter] = useState('all');

  const setStatus = async (quote, status) => {
    try {
      await axios.patch(`${API}/quotes/${quote.id}`, { status });
      setQuotes((prev) => prev.map((q) => (q.id === quote.id ? { ...q, status } : q)));
      toast.success(`Quote marked ${QUOTE_STATUS_META[status]?.label || status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update quote');
    }
  };

  const remove = async (quote) => {
    if (!window.confirm(`Delete quote from ${quote.contact_name || quote.email}?`)) return;
    try {
      await axios.delete(`${API}/quotes/${quote.id}`);
      setQuotes((prev) => prev.filter((q) => q.id !== quote.id));
      toast.success('Quote deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete quote');
    }
  };

  const filtered = filter === 'all' ? quotes : quotes.filter((q) => q.status === filter);
  const countBy = (s) => quotes.filter((q) => q.status === s).length;

  if (!quotes.length) {
    return (
      <div className="text-center py-20 bg-white border border-zinc-200" data-testid="quotes-empty">
        <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-zinc-700">No quote requests yet</h3>
        <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
          When a shipper submits a request via the Quote Calculator, it will appear here so you can respond and win the freight.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quotes-tab">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 bg-white border border-zinc-200 p-3">
        <FilterPill active={filter === 'all'}       onClick={() => setFilter('all')}       label="All"       count={quotes.length} testId="quotes-filter-all" />
        <FilterPill active={filter === 'pending'}   onClick={() => setFilter('pending')}   label="Pending"   count={countBy('pending')}   testId="quotes-filter-pending" />
        <FilterPill active={filter === 'contacted'} onClick={() => setFilter('contacted')} label="Contacted" count={countBy('contacted')} testId="quotes-filter-contacted" />
        <FilterPill active={filter === 'won'}       onClick={() => setFilter('won')}       label="Won"       count={countBy('won')}       testId="quotes-filter-won" />
        <FilterPill active={filter === 'dismissed'} onClick={() => setFilter('dismissed')} label="Dismissed" count={countBy('dismissed')} testId="quotes-filter-dismissed" />
      </div>

      {filtered.map((q) => {
        const meta = QUOTE_STATUS_META[q.status] || QUOTE_STATUS_META.pending;
        return (
          <div key={q.id} className="bg-white border border-zinc-200 p-5" data-testid={`quote-row-${q.id}`}>
            <div className="flex justify-between items-start gap-3 mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-black text-zinc-900 text-lg">{q.contact_name || 'Unknown contact'}</h3>
                  <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${meta.color}`}>
                    {meta.label}
                  </span>
                </div>
                {q.company_name && (
                  <p className="text-sm text-zinc-600 font-bold mt-0.5">{q.company_name}</p>
                )}
                <div className="flex items-center gap-3 text-sm text-zinc-500 mt-1 flex-wrap">
                  {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-1 hover:text-red-600"><Mail className="w-3.5 h-3.5" />{q.email}</a>}
                  {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-1 hover:text-red-600"><Phone className="w-3.5 h-3.5" />{q.phone}</a>}
                  {q.created_at && (
                    <span className="text-xs text-zinc-400">{new Date(q.created_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4 text-sm bg-zinc-50 border border-zinc-200 p-3">
              <Field icon={MapPin}   label="Origin"       value={q.origin} />
              <Field icon={MapPin}   label="Destination"  value={q.destination} />
              <Field icon={Weight}   label="Freight"      value={`${q.freight_type || '—'} · ${q.weight ? `${Number(q.weight).toLocaleString()} lb` : '—'}`} />
              <Field icon={Calendar} label="Pickup"       value={q.pickup_date || '—'} />
            </div>

            {q.special_requirements && (
              <div className="mt-3 text-sm text-zinc-700 bg-amber-50 border border-amber-200 p-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-amber-700 mb-1">Special requirements</div>
                {q.special_requirements}
              </div>
            )}

            {q.dispatch_notes && (
              <div className="mt-3 text-sm text-zinc-700 bg-blue-50 border border-blue-200 p-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-blue-700 mb-1">Dispatch notes</div>
                {q.dispatch_notes}
              </div>
            )}

            {/* Action bar */}
            <div className="flex flex-wrap gap-2 mt-4">
              {q.status !== 'contacted' && (
                <ActionBtn onClick={() => setStatus(q, 'contacted')} color="blue" testId={`quote-mark-contacted-${q.id}`}>
                  Mark contacted
                </ActionBtn>
              )}
              {q.status !== 'won' && (
                <ActionBtn onClick={() => setStatus(q, 'won')} color="green" testId={`quote-mark-won-${q.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mark won
                </ActionBtn>
              )}
              {q.status !== 'dismissed' && (
                <ActionBtn onClick={() => setStatus(q, 'dismissed')} color="zinc" testId={`quote-mark-dismissed-${q.id}`}>
                  Dismiss
                </ActionBtn>
              )}
              {q.status !== 'pending' && (
                <ActionBtn onClick={() => setStatus(q, 'pending')} color="amber" testId={`quote-mark-pending-${q.id}`}>
                  Reopen
                </ActionBtn>
              )}
              <ActionBtn onClick={() => remove(q)} color="red" testId={`quote-delete-${q.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </ActionBtn>
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="text-center py-10 bg-white border border-zinc-200 text-sm text-zinc-500">
          No quotes match this filter.
        </div>
      )}
    </div>
  );
};

const Field = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-zinc-400">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    <p className="font-bold text-zinc-900 mt-0.5">{value || '—'}</p>
  </div>
);

const FilterPill = ({ active, onClick, label, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
      active ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
    }`}
  >
    {label}
    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-black ${
      active ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-600'
    }`}>
      {count}
    </span>
  </button>
);

const ActionBtn = ({ onClick, color, testId, children }) => {
  const cls = {
    blue:  'border-blue-300 text-blue-700 hover:bg-blue-50',
    green: 'border-green-300 text-green-700 hover:bg-green-50',
    zinc:  'border-zinc-300 text-zinc-600 hover:bg-zinc-50',
    amber: 'border-amber-300 text-amber-700 hover:bg-amber-50',
    red:   'border-red-300 text-red-700 hover:bg-red-50',
  }[color] || 'border-zinc-300 text-zinc-600';
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors ${cls}`}
    >
      {children}
    </button>
  );
};

export const ContactsTab = ({ contacts }) => {
  if (contacts.length === 0) {
    return (
      <div className="text-center py-20 bg-white border border-zinc-200">
        <MessageSquare className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-zinc-700">No contact messages yet</h3>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {contacts.map((contact) => (
        <div key={contact.id} className="bg-white border border-zinc-200 p-6">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-bold text-zinc-900">{contact.name}</h3>
              <p className="text-sm text-zinc-500">{contact.email} {contact.phone && `\u2022 ${contact.phone}`}</p>
            </div>
            <span className="text-xs text-zinc-400">
              {new Date(contact.created_at).toLocaleDateString()}
            </span>
          </div>
          <p className="font-medium text-zinc-700 mb-2">{contact.subject}</p>
          <p className="text-sm text-zinc-600">{contact.message}</p>
        </div>
      ))}
    </div>
  );
};

export const ApplicationsTab = ({ applications }) => {
  if (applications.length === 0) {
    return (
      <div className="text-center py-20 bg-white border border-zinc-200">
        <Users className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-zinc-700">No job applications yet</h3>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.map((app) => (
        <div key={app.id} className="bg-white border border-zinc-200 p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-zinc-900">{app.full_name}</h3>
              <p className="text-sm text-zinc-500">{app.email} &bull; {app.phone}</p>
            </div>
            <span className={`px-2 py-1 text-xs font-bold uppercase ${
              app.status === 'received' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
            }`}>
              {app.status}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-500">Position:</span>
              <p className="font-medium">{app.position}</p>
            </div>
            <div>
              <span className="text-zinc-500">Experience:</span>
              <p className="font-medium">{app.experience} years</p>
            </div>
          </div>
          {app.cover_letter && (
            <p className="mt-4 text-sm text-zinc-600 bg-zinc-50 p-3">{app.cover_letter}</p>
          )}
        </div>
      ))}
    </div>
  );
};
